import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Award,
  BadgeCheck,
  Camera,
  CheckCircle2,
  ChevronRight,
  Clock3,
  GraduationCap,
  Image as ImageIcon,
  Instagram,
  Layers3,
  MessageCircle,
  MonitorPlay,
  PenTool,
  Sparkles,
  Target,
  Trophy,
  Users2,
  Wand2,
  Youtube,
  Zap,
  Lock,
  ShieldCheck,
  Gift,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const PAYMENT_URL = "https://pay.cakto.com.br/34ideo4_882212";
const MENTOR_URL = "https://instagram.com/andresouzafotografo";
const YOUTUBE_URL = "https://www.youtube.com/@masterescola";

const audiences = [
  {
    icon: Camera,
    title: "Fotógrafos iniciantes",
    text: "Para quem sente que a foto até nasce boa, mas perde força na finalização e precisa de um fluxo claro no Lightroom e Photoshop.",
  },
  {
    icon: Target,
    title: "Fotógrafos em crescimento",
    text: "Para quem já atende clientes e quer padronizar cor, pele, contraste e entrega com mais consistência de portfólio.",
  },
  {
    icon: PenTool,
    title: "Editores e retocadores",
    text: "Para quem quer ganhar velocidade, refinar acabamento e criar um tratamento de pele bonito sem deixar a imagem artificial.",
  },
  {
    icon: Sparkles,
    title: "Nível avançado",
    text: "Para quem já edita, mas quer lapidar assinatura visual, dominar decisões de luz/cor e evoluir com feedback de comunidade.",
  },
];

const modules = [
  {
    icon: Layers3,
    title: "Lightroom do zero ao fluxo profissional",
    text: "Organização, correção de luz, curva de tons, cor, presets com critério e exportação para entregar com padrão.",
  },
  {
    icon: Wand2,
    title: "Photoshop para acabamento real",
    text: "Camadas, máscaras, ajustes locais, limpeza, composição e finalização sem depender de tentativa e erro.",
  },
  {
    icon: ImageIcon,
    title: "Tratamento de pele natural",
    text: "Técnicas para remover distrações, preservar textura e valorizar o rosto sem apagar identidade.",
  },
  {
    icon: Users2,
    title: "Comunidade e suporte",
    text: "Espaço para postar fotos, receber direção, acompanhar evoluções e tirar dúvidas durante o processo.",
  },
];

const outcomes = [
  "Transformar fotos cruas em imagens com impacto comercial.",
  "Criar consistência de cor para ensaios, retratos e editoriais.",
  "Tratar pele com acabamento limpo e aparência humana.",
  "Entender quando usar Lightroom, Photoshop e recursos de IA.",
  "Montar uma rotina de edição mais rápida e menos travada.",
  "Receber suporte e feedback para continuar evoluindo.",
];

const mentorHighlights = [
  {
    icon: Users2,
    title: "+1.500 alunos",
    text: "Fotógrafos e editores formados em aulas, mentorias e treinamentos.",
  },
  {
    icon: Trophy,
    title: "9+ anos de carreira",
    text: "Atuação profissional com prêmios e reconhecimento na fotografia.",
  },
  {
    icon: Award,
    title: "Palestrante e mentor",
    text: "Direção prática para fotógrafos Master evoluírem técnica, olhar e negócio.",
  },
  {
    icon: GraduationCap,
    title: "Formação Infnet + Adobe",
    text: "Formado pela Faculdade Infnet de Tecnologia, com trajetória conectada ao ecossistema Adobe.",
  },
];

const proofImages = [
  {
    src: "/edicao-master/before-after-1.jpg",
    alt: "Antes e depois de retrato com fundo colorido e tratamento de pele no Edição Master.",
  },
  {
    src: "/edicao-master/before-after-2.jpg",
    alt: "Antes e depois de fotografia noturna com correção de luz e cor no Edição Master.",
  },
  {
    src: "/edicao-master/before-after-3.jpg",
    alt: "Antes e depois de retrato externo com composição e luz quente no Edição Master.",
  },
  {
    src: "/edicao-master/before-after-4.jpg",
    alt: "Antes e depois de retrato urbano com tratamento de cor e contraste no Edição Master.",
  },
];

const faqItems = [
  {
    question: "O curso serve para quem está começando?",
    answer:
      "Sim. O método começa pelos fundamentos e avança até uma edição completa em Lightroom e Photoshop, com um passo a passo claro para quem nunca abriu esses programas.",
  },
  {
    question: "Quem já edita também aproveita?",
    answer:
      "Muito. Quem já trabalha com edição ganha repertório de acabamento, tratamento de pele, consistência visual e feedback da comunidade para refinar o próprio estilo e ganhar velocidade.",
  },
  {
    question: "Quanto custa e como posso pagar?",
    answer:
      "São 12x de R$ 20,35 ou R$ 197,00 à vista, no cartão, Pix ou boleto. O pagamento é processado com segurança no checkout oficial da Cakto e o acesso é liberado logo após a confirmação.",
  },
  {
    question: "E se eu não gostar? Tenho garantia?",
    answer:
      "Sim. Você tem 7 dias de garantia. Entrou, acessou e sentiu que não é para você? É só pedir o reembolso dentro do prazo, sem burocracia e sem risco.",
  },
  {
    question: "O suporte acontece onde?",
    answer:
      "Dentro da comunidade do curso: você posta suas fotos, acompanha a evolução de outros alunos, troca referências e recebe suporte para as dúvidas do processo.",
  },
  {
    question: "Como faço a inscrição?",
    answer:
      "Clique em qualquer botão de acesso. Você vai para o checkout oficial na Cakto, conclui o pagamento e recebe a liberação do acesso por lá.",
  },
];

const offerIncludes = [
  "Lightroom do zero ao fluxo profissional",
  "Photoshop para acabamento e finalização real",
  "Tratamento de pele natural, sem aparência de plástico",
  "Edição completa com apoio de recursos de IA",
  "Comunidade para postar fotos e receber direção",
  "Suporte para as dúvidas do seu processo",
  "Acesso imediato, no seu ritmo e de onde quiser",
  "Bônus: Presets Master e Ações para Photoshop",
  "Bônus: Curso de como vender e entregar fotos premium",
  "7 dias de garantia incondicional",
];

const bonuses = [
  {
    icon: Wand2,
    tag: "Bônus 1",
    title: "Presets Master e Ações para Photoshop",
    text: "Pacote pronto para acelerar sua edição: presets de Lightroom e ações de Photoshop que aplicam cor, contraste e acabamento em poucos cliques.",
  },
  {
    icon: Trophy,
    tag: "Bônus 2",
    title: "Curso de como vender fotos extras e entregar premium",
    text: "Aumente o valor de cada trabalho vendendo fotos extras e entregando as imagens de um jeito premium que o cliente percebe e paga mais.",
  },
];

function CheckoutButton({ className = "", label = "Garantir meu acesso" }: { className?: string; label?: string }) {
  return (
    <Button
      asChild
      size="lg"
      className={`min-h-12 bg-[#0b7f87] px-6 text-base font-bold text-white shadow-lg shadow-black/30 transition duration-200 hover:bg-[#0b7f87]/90 hover:shadow-xl focus-visible:ring-[#0b7f87] ${className}`}
    >
      <a href={PAYMENT_URL} target="_blank" rel="noreferrer" aria-label={`${label} no checkout oficial da Cakto`}>
        {label}
        <ArrowRight className="h-5 w-5" aria-hidden="true" />
      </a>
    </Button>
  );
}

export default function EdicaoMasterPage() {
  return (
    <>
      <Helmet>
        <title>Edição Master - Lightroom & Photoshop</title>
        <meta
          name="description"
          content="Página de vendas do curso Edição Master: Lightroom, Photoshop, tratamento de pele, comunidade de postagem de fotos e suporte."
        />
      </Helmet>

      <main className="min-h-screen overflow-x-hidden bg-black text-white">
        <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-md">
          <div className="mx-auto flex min-h-16 w-full max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
            <a href="#topo" className="flex items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0b7f87]">
              <img
                src="/edicao-master/logo.png"
                alt="Edição Master Lightroom e Photoshop"
                width={160}
                height={90}
                className="h-12 w-36 object-contain sm:w-44"
              />
            </a>
            <nav aria-label="Navegação da página" className="hidden items-center gap-6 text-sm font-medium text-white/75 md:flex">
              <a className="transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0b7f87]" href="#conteudo">
                Conteúdo
              </a>
              <a className="transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0b7f87]" href="#comunidade">
                Comunidade
              </a>
              <a className="transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0b7f87]" href="#oferta">
                Investimento
              </a>
              <a className="transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0b7f87]" href="#duvidas">
                Dúvidas
              </a>
            </nav>
            <CheckoutButton className="hidden sm:inline-flex" label="Inscrever-se" />
          </div>
        </header>

        <section
          id="topo"
          className="relative flex min-h-[86svh] items-center overflow-hidden bg-black pt-24"
          style={{
            backgroundImage: "linear-gradient(90deg, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.86) 36%, rgba(0,0,0,0.32) 72%, rgba(0,0,0,0.76) 100%), url('/edicao-master/hero.jpg')",
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black to-transparent" aria-hidden="true" />
          <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 pb-14 pt-12 sm:px-6 lg:grid-cols-[minmax(0,0.92fr)_minmax(320px,0.58fr)] lg:px-8">
            <div className="max-w-3xl animate-fade-in-up">
              <Badge className="mb-6 border-[#0b7f87]/30 bg-[#0b7f87]/80 px-3 py-1 text-xs uppercase tracking-[0.24em] text-white">
                Lightroom + Photoshop
              </Badge>
              <h1 className="max-w-3xl text-5xl font-black leading-[0.94] text-white sm:text-6xl lg:text-8xl">
                Edição Master
              </h1>
              <p className="mt-6 max-w-2xl text-xl leading-8 text-white/[0.86] sm:text-2xl">
                Domine Lightroom e Photoshop com um método direto e transforme fotos cruas em entregas com cor, pele e acabamento profissional, mesmo que hoje a edição ainda trave você.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <CheckoutButton />
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="min-h-12 border-white/25 bg-white/5 px-6 text-base text-white backdrop-blur transition duration-200 hover:bg-white/[0.12] hover:text-white"
                >
                  <a href="#conteudo">
                    Ver o que vou aprender
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </a>
                </Button>
              </div>
              <p className="mt-4 text-sm text-white/[0.72]">
                <span className="font-semibold text-white">12x de R$ 20,35</span> ou R$ 197 à vista. Acesso imediato e 7 dias de garantia.
              </p>
              <div className="mt-8 grid gap-3 text-sm text-white/75 sm:grid-cols-3">
                <span className="flex items-center gap-2">
                  <Clock3 className="h-4 w-4 text-[#0b7f87]" aria-hidden="true" />
                  21 dias ou menos
                </span>
                <span className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-[#0b7f87]" aria-hidden="true" />
                  Suporte na comunidade
                </span>
                <span className="flex items-center gap-2">
                  <BadgeCheck className="h-4 w-4 text-[#0b7f87]" aria-hidden="true" />
                  Do básico ao avançado
                </span>
              </div>
            </div>
            <div className="hidden items-end justify-end lg:flex" aria-hidden="true">
              <div className="max-w-sm border-l border-[#0b7f87]/30 pl-6 text-right text-sm leading-6 text-white/70">
                Para fotógrafos e editores que querem parar de depender de sorte na edição e começar a construir uma assinatura visual repetível.
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-black/10 bg-white py-4">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-4 text-sm font-semibold text-black sm:px-6 lg:px-8">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#0b7f87]" aria-hidden="true" />
              Lightroom
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#0b7f87]" aria-hidden="true" />
              Photoshop
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#0b7f87]" aria-hidden="true" />
              Tratamento de pele
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#0b7f87]" aria-hidden="true" />
              Fotos na comunidade
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#0b7f87]" aria-hidden="true" />
              Suporte
            </span>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 md:py-20 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-white">Para quem é</p>
              <h2 className="mt-4 text-3xl font-bold leading-tight text-white sm:text-5xl">
                Feito para quem vive de imagem, em qualquer fase da jornada.
              </h2>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {audiences.map((audience) => {
                const Icon = audience.icon;

                return (
                  <article key={audience.title} className="rounded-lg border border-white/10 bg-white/[0.045] p-6 transition duration-200 hover:-translate-y-1 hover:border-[#0b7f87]/[0.45] hover:bg-white/[0.07]">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-md bg-[#0b7f87]/20 text-white">
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{audience.title}</h3>
                    <p className="mt-3 leading-7 text-white/[0.68]">{audience.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="conteudo" className="bg-[#f5f2ea] px-4 py-16 text-black sm:px-6 md:py-20 lg:px-8 lg:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#0b7f87]">O que você vai dominar</p>
              <h2 className="mt-4 text-3xl font-black leading-tight sm:text-5xl">
                Edição completa, do ajuste global ao acabamento fino.
              </h2>
              <p className="mt-5 text-lg leading-8 text-black/70">
                O curso organiza o caminho para você sair da imagem crua, passar por cor e luz, refinar pele e fechar a entrega com segurança estética.
              </p>
              <div className="mt-8 grid gap-3">
                {outcomes.map((outcome) => (
                  <div key={outcome} className="flex gap-3 rounded-md border border-black/10 bg-white p-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-[#0b7f87]" aria-hidden="true" />
                    <span className="leading-7 text-black/80">{outcome}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {modules.map((module) => {
                const Icon = module.icon;

                return (
                  <article key={module.title} className="rounded-lg border border-black/10 bg-white p-6 shadow-sm">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-md bg-[#0b7f87]/10 text-[#0b7f87]">
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <h3 className="text-xl font-black leading-snug">{module.title}</h3>
                    <p className="mt-3 leading-7 text-black/70">{module.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 md:py-20 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.22em] text-white">Veja o resultado</p>
                <h2 className="mt-4 text-3xl font-black leading-tight sm:text-5xl">
                  O valor da edição aparece quando o antes e depois fala sozinho.
                </h2>
                <p className="mt-5 text-lg leading-8 text-white/70">
                  A proposta não é copiar um preset. É entender leitura de imagem, intenção de cor, pele, profundidade e finalização.
                </p>
              </div>
              <img
                src="/edicao-master/course-screen.jpg"
                alt="Tela do curso Edição Master com módulos de Photoshop, Lightroom, tratamento de pele e edição completa com IA."
                width={1600}
                height={1280}
                loading="lazy"
                className="w-full rounded-lg border border-white/10 object-cover shadow-2xl shadow-black/40"
              />
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {proofImages.map((image) => (
                <img
                  key={image.src}
                  src={image.src}
                  alt={image.alt}
                  width={1100}
                  height={1100}
                  loading="lazy"
                  className="aspect-square w-full rounded-lg border border-white/10 object-cover shadow-lg shadow-black/30"
                />
              ))}
            </div>
          </div>
        </section>

        <section id="comunidade" className="border-y border-white/10 bg-black px-4 py-16 sm:px-6 md:py-20 lg:px-8 lg:py-24">
          <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-white">Você não edita sozinho</p>
              <h2 className="mt-4 text-3xl font-black leading-tight sm:text-5xl">
                Aprender edição fica mais rápido quando você posta, compara e recebe direção.
              </h2>
              <p className="mt-5 max-w-3xl text-lg leading-8 text-white/[0.72]">
                A comunidade existe para tirar a edição do isolamento: você compartilha fotos, observa ajustes de outros alunos, recebe suporte e acompanha sua evolução com mais clareza.
              </p>
            </div>
            <div className="grid gap-4">
              {[
                "Postagem de fotos para análise e evolução.",
                "Suporte para dúvidas de Lightroom, Photoshop e acabamento.",
                "Referências práticas para sair da edição sem direção.",
                "Ambiente para fotógrafos e editores em diferentes níveis.",
              ].map((item) => (
                <div key={item} className="flex gap-3 rounded-lg border border-[#0b7f87]/20 bg-black/[0.22] p-5">
                  <MessageCircle className="mt-0.5 h-5 w-5 flex-none text-white" aria-hidden="true" />
                  <p className="leading-7 text-white/[0.82]">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 md:py-20 lg:px-8 lg:py-24">
          <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
            <div className="overflow-hidden rounded-lg border border-white/10 bg-white/[0.045]">
              <img
                src="/edicao-master/foto-andre.jpeg"
                alt="André Souza palestrando em um auditório para fotógrafos."
                width={1280}
                height={853}
                loading="lazy"
                className="aspect-[16/10] w-full object-cover object-center"
              />
              <div className="p-6">
                <MonitorPlay className="h-10 w-10 text-[#0b7f87]" aria-hidden="true" />
                <p className="mt-6 text-sm font-bold uppercase tracking-[0.22em] text-white">Quem vai te ensinar</p>
                <h2 className="mt-3 text-3xl font-black leading-tight">André Souza, professor e mentor de fotógrafos Master.</h2>
                <p className="mt-4 leading-8 text-white/[0.72]">
                  Criador da Escola Master de Fotografia e do canal Master Escola, André conecta técnica, carreira e direção visual para ajudar fotógrafos a editarem com método e autoridade.
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                  <Button
                    asChild
                    variant="outline"
                    className="min-h-11 border-white/20 bg-white/5 text-white hover:bg-white/[0.12] hover:text-white"
                  >
                    <a href={MENTOR_URL} target="_blank" rel="noreferrer" aria-label="Abrir Instagram de André Souza">
                      <Instagram className="h-4 w-4" aria-hidden="true" />
                      Instagram
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="min-h-11 border-white/20 bg-white/5 text-white hover:bg-white/[0.12] hover:text-white"
                  >
                    <a href={YOUTUBE_URL} target="_blank" rel="noreferrer" aria-label="Abrir canal Master Escola no YouTube">
                      <Youtube className="h-4 w-4" aria-hidden="true" />
                      Canal Master Escola
                    </a>
                  </Button>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.03] p-6 sm:p-8">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-white">Experiência que te guia</p>
              <h2 className="mt-4 text-3xl font-black leading-tight sm:text-5xl">
                Mais de 1.500 alunos, prêmios na fotografia e mais de 9 anos de carreira.
              </h2>
              <p className="mt-5 text-lg leading-8 text-white/[0.72]">
                O Edição Master nasce da experiência de quem ensina fotografia, acompanha alunos na prática e conhece o caminho entre técnica, portfólio e entrega profissional.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {mentorHighlights.map((item) => {
                  const Icon = item.icon;

                  return (
                    <div key={item.title} className="rounded-md border border-white/10 bg-black/[0.24] p-4">
                      <Icon className="h-6 w-6 text-[#0b7f87]" aria-hidden="true" />
                      <h3 className="mt-4 text-lg font-black text-white">{item.title}</h3>
                      <p className="mt-2 leading-7 text-white/[0.72]">{item.text}</p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <CheckoutButton label="Ir para o checkout" />
                <p className="text-sm leading-6 text-white/[0.58]">Pagamento processado no checkout oficial da Cakto.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 md:py-20 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-2xl">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-white">Bônus de matrícula</p>
              <h2 className="mt-4 text-3xl font-black leading-tight text-white sm:text-5xl">
                Ao entrar hoje, você também leva dois bônus para acelerar resultado e faturamento.
              </h2>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {bonuses.map((bonus) => {
                const Icon = bonus.icon;

                return (
                  <article key={bonus.title} className="relative overflow-hidden rounded-lg border border-[#0b7f87]/25 bg-gradient-to-br from-[#0b7f87]/[0.08] to-white/[0.02] p-6">
                    <span className="inline-flex items-center gap-2 rounded-full border border-[#0b7f87]/40 bg-[#0b7f87]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-white">
                      <Gift className="h-3.5 w-3.5" aria-hidden="true" />
                      {bonus.tag}
                    </span>
                    <div className="mt-5 flex h-12 w-12 items-center justify-center rounded-md bg-[#0b7f87]/15 text-white">
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <h3 className="mt-5 text-xl font-black text-white">{bonus.title}</h3>
                    <p className="mt-3 leading-7 text-white/[0.72]">{bonus.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="oferta" className="border-y border-white/10 bg-black px-4 py-16 sm:px-6 md:py-20 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-white">Investimento</p>
              <h2 className="mt-4 text-3xl font-black leading-tight text-white sm:text-5xl">
                Acesso completo ao Edição Master
              </h2>
              <p className="mt-5 text-lg leading-8 text-white/[0.72]">
                Tudo o que você precisa para sair da edição por tentativa e erro e construir um acabamento profissional e repetível.
              </p>
            </div>

            <div className="mt-10 grid gap-5 lg:grid-cols-[1.05fr_0.95fr] lg:items-stretch">
              <div className="rounded-lg border border-white/10 bg-white/[0.045] p-6 sm:p-8">
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-white">O que está incluído</p>
                <ul className="mt-6 grid gap-3">
                  {offerIncludes.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-[#0b7f87]" aria-hidden="true" />
                      <span className="leading-7 text-white/[0.82]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex h-full flex-col rounded-lg border border-[#0b7f87]/40 bg-gradient-to-b from-[#0b7f87]/20 to-black p-6 shadow-2xl shadow-black/30 sm:p-8">
                <Badge className="w-fit border-[#0b7f87]/30 bg-[#0b7f87]/80 px-3 py-1 text-xs uppercase tracking-[0.24em] text-white">
                  Matrícula aberta
                </Badge>
                <p className="mt-6 text-sm font-medium text-white/60">Comece hoje por</p>
                <div className="mt-2 flex items-end gap-2">
                  <span className="pb-1 text-2xl font-bold text-white/80">12x</span>
                  <span className="text-6xl font-black leading-none text-white">R$ 20,35</span>
                </div>
                <p className="mt-3 text-lg text-white/[0.85]">
                  ou <span className="font-black text-white">R$ 197,00</span> à vista
                </p>
                <p className="mt-1 text-sm text-white/[0.55]">no cartão, Pix ou boleto. Menos de R$ 0,68 por dia.</p>

                <CheckoutButton className="mt-7 w-full" label="Garantir meu acesso" />

                <div className="mt-6 grid gap-3 border-t border-white/10 pt-6 text-sm text-white/[0.7]">
                  <span className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 flex-none text-[#0b7f87]" aria-hidden="true" />
                    7 dias de garantia incondicional
                  </span>
                  <span className="flex items-center gap-2">
                    <Zap className="h-4 w-4 flex-none text-[#0b7f87]" aria-hidden="true" />
                    Acesso imediato após a confirmação
                  </span>
                  <span className="flex items-center gap-2">
                    <Lock className="h-4 w-4 flex-none text-[#0b7f87]" aria-hidden="true" />
                    Pagamento seguro no checkout da Cakto
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="duvidas" className="bg-[#f5f2ea] px-4 py-16 text-black sm:px-6 md:py-20 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-4xl">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#0b7f87]">Perguntas frequentes</p>
            <h2 className="mt-4 text-3xl font-black leading-tight sm:text-5xl">Antes de entrar.</h2>
            <div className="mt-8 grid gap-4">
              {faqItems.map((item) => (
                <article key={item.question} className="rounded-lg border border-black/10 bg-white p-6 shadow-sm">
                  <h3 className="text-xl font-black">{item.question}</h3>
                  <p className="mt-3 leading-8 text-black/70">{item.answer}</p>
                </article>
              ))}
            </div>
            <div className="mt-10 rounded-lg bg-black p-6 text-white sm:p-8">
              <h3 className="text-2xl font-black">Pronto para editar com método?</h3>
              <p className="mt-3 max-w-2xl leading-8 text-white/[0.72]">
                Garanta seu acesso por 12x de R$ 20,35 (ou R$ 197 à vista), com 7 dias de garantia. Comece hoje a construir um acabamento mais consistente para suas fotos.
              </p>
              <CheckoutButton className="mt-6" label="Garantir acesso ao curso" />
            </div>
          </div>
        </section>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-black/90 p-3 backdrop-blur-md sm:hidden">
        <CheckoutButton className="w-full" label="Garantir acesso" />
      </div>
    </>
  );
}
